import React from 'react'
import { Navbar, Nav } from 'react-bootstrap'
import styled from 'styled-components'
import {
	Subscription,
	LiveDrive,
	FoundersLogo,
	Hamburger,
	LogoHoriz,
	MapIcon,
	SoftwareTracker,
	McpIcon,
} from '../../utility/icons'
import { useIsActive } from '../../utility/hooks'
import { isTesla } from '../../utility/helpers'

// [claude] Top navigation bar for the Ink design system (replaced the fixed 125px
// left rail, 2026-07). Fixed 64px bar with a hairline seam; 16px brand icons +
// labels, hamburger + stacked panel under 1200px. Active/hover states follow
// the Software Tracker segmented-control language: muted -> white, no underlines.
// Themed via the ScreenWrapper ThemeProvider (p.theme).

const activePaths = {
	map: ['charger_map', '^/map'],
	team: ['team'],
	founders: ['founders'],
	dash: ['dash'],
	subscribe: ['subscribe', 'subscription', 'gift'],
	faq: ['faq'],
	software: ['software-tracker'],
	mcp: ['mcp'],
}

const BREAK = '991.98px'

const StyledNav = styled(Navbar)`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 1000;
	height: 64px;
	padding: 0;
	background-color: ${({ theme }) => theme.base};
	border-bottom: 1px solid ${({ theme, $scrolled }) => ($scrolled ? theme.border : 'transparent')};
	transition: border-bottom-color 0.3s ease;
	@media screen and (max-width: ${BREAK}) {
		height: auto;
		min-height: 64px;
	}
`

const NavInner = styled.div`
	width: 100%;
	max-width: 1320px;
	margin: 0 auto;
	padding: 0 24px;
	min-height: 64px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
`

const LinkRow = styled(Nav)`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 4px;
	margin-left: auto;
	width: 100%;
	@media screen and (max-width: ${BREAK}) {
		flex-direction: column;
		align-items: stretch;
		gap: 2px;
		margin: 0;
		padding: 8px 0 16px;
		width: 100%;
	}
`

const TopLink = styled(Nav.Link)`
	display: inline-flex;
	align-items: center;
	gap: 7px;
	text-decoration: none !important;
	font-family: ${({ theme }) => theme.fontFamily};
	font-size: 13.5px;
	font-weight: 500;
	line-height: 20px;
	padding: 6px 11px !important;
	border-radius: 8px;
	& > svg {
		width: 16px;
		height: 16px;
		flex-shrink: 0;
	}
	color: ${({ theme, $active }) => ($active ? theme.text : theme.textSecondary)} !important;
	background-color: ${({ theme, $active }) => ($active ? 'rgba(127, 127, 127, 0.12)' : 'transparent')};
	transition: color ${({ theme }) => theme.motion.fast}, background-color ${({ theme }) => theme.motion.fast};
	&:hover {
		color: ${({ theme }) => theme.text} !important;
	}
	/* no outlines on the nav cells - state is expressed via background only */
	outline: none !important;
	box-shadow: none !important;
	&:focus {
		outline: none;
	}
	&:focus-visible {
		outline: none;
		color: ${({ theme }) => theme.text} !important;
		background-color: rgba(127, 127, 127, 0.12);
	}
	@media screen and (max-width: ${BREAK}) {
		padding: 10px 12px !important;
	}
`

const StyledCollapse = styled(Navbar.Collapse)`
	@media screen and (max-width: ${BREAK}) {
		background-color: ${({ theme }) => theme.base};
	}
`

const LogoLink = styled(Nav.Link)`
	padding: 0 !important;
	margin-right: 48px;
	text-decoration: none !important;
	/* wordmark svg is hardcoded white; invert it for light mode */
	filter: ${({ theme }) => (theme.name === 'light' ? 'invert(0.88)' : 'none')};
	display: flex;
	align-items: center;
	outline: none;
	box-shadow: none !important;
	&:focus {
		outline: none;
	}
	&:focus-visible {
		outline: 2px solid ${({ theme }) => theme.borderFocus};
		outline-offset: 4px;
		border-radius: 6px;
	}
`

const StyledToggle = styled(Navbar.Toggle)`
	margin-left: auto;
	border: none;
	filter: ${({ theme }) => (theme.name === 'light' ? 'invert(0.88)' : 'none')};
	outline: none !important;
	box-shadow: none !important;
	border-radius: 8px;
	&:focus-visible {
		background-color: rgba(127, 127, 127, 0.12);
	}
`

// Icons ride along at 16px and inherit the link's state color via currentColor
// (their built-in active-blue is bypassed by passing no activePaths).
const NavItem = ({ href, paths, icon: Icon, children }) => {
	const isActive = useIsActive(paths || [])
	return (
		<TopLink href={href} $active={isActive}>
			{Icon && <Icon fill='currentColor' />}
			{children}
		</TopLink>
	)
}

export default function Header() {
	const [scrolled, setScrolled] = React.useState(false)
	React.useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 8)
		onScroll()
		window.addEventListener('scroll', onScroll, { passive: true })
		return () => window.removeEventListener('scroll', onScroll)
	}, [])
	return (
		<StyledNav id='navbar' collapseOnSelect expand='lg' $scrolled={scrolled}>
			<NavInner>
				<LogoLink href='/' aria-label='TezLab home'>
					<LogoHoriz />
				</LogoLink>
				<StyledToggle aria-controls='responsive-navbar-nav' aria-label='Toggle navigation'>
					<Hamburger />
				</StyledToggle>
				<StyledCollapse id='responsive-navbar-nav'>
					<LinkRow>
						<NavItem href='/subscribe' paths={activePaths.subscribe} icon={Subscription}>Membership</NavItem>
						<NavItem href='/founders' paths={activePaths.founders} icon={FoundersLogo}>Founders Series</NavItem>
						<NavItem href='/software-tracker/tesla' paths={activePaths.software} icon={SoftwareTracker}>Software Tracker</NavItem>
						<NavItem href='/mcp' paths={activePaths.mcp} icon={McpIcon}>MCP</NavItem>
						<NavItem href='/map' paths={activePaths.map} icon={MapIcon}>Charger Map</NavItem>
						{isTesla() && (
							<NavItem href='/dash' paths={activePaths.dash} icon={LiveDrive}>Dashboard</NavItem>
						)}
					</LinkRow>
				</StyledCollapse>
			</NavInner>
		</StyledNav>
	)
}
